import { ValidBookingStatusesNew } from "@/api_framework/api_modals/Patients";
import { useLoader } from "@/app/hooks/useLoader";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ROUTES } from "@/app/routes";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { IPatientWaitingListState, setPWLFilters } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { IPWLStatsListProps } from "../interface";
import { IOrderManagementState } from "@/app_redux/reducers/slices/auth/order_management_slice";
import { useRouter } from "@/app/hooks/useRouter";
import { useNavigate } from "react-router";
import StatsCard from "@/app/components/ui/Reusable/StatsCard";
import { ILabReportListState } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import StatsCardsSkeleton from "@/app/components/ui/Skeletons/StatsCardsSkeleton";

const emptyStates = [
  {
    Total: {
      count: 0,
      id: null,
    },
    Upcoming: {
      count: 0,
      id: "1",
    },
    Waiting: {
      count: 0,
      id: "2",
    },
    Attending: {
      count: 0,
      id: "3",
    },
    Completed: {
      count: 0,
      id: "4",
    },
    Cancelled: {
      count: 0,
      id: "5",
    },
  },
];

const PatientWaitingListStats: React.FC<IPWLStatsListProps> = ({ setStatCard, currentStat }) => {
  /* Instantiations Starts */
  const dispatch = useAppDispatch();
  const { stats: statsMapping } = useMappedConstants();
  const isStatsLoading = useLoader("isPWLStatsFetchInProgress");

  const [initialFetched, setInitialFetched] = useState(false);
  const [hasRequestedStatsAtLeastOnce, setHasRequestedStatsAtLeastOnce] = useState<boolean>(false);

  /* Instantiations Ends */
  const navigate = useNavigate();
  /* Redux State Starts */
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.PATIENT_WAITING_LIST.id);
  const { waitingStats, filters } = routeState.state as IPatientWaitingListState;
  const hasStatsData = Array.isArray(waitingStats) && waitingStats.length > 0;

  const { getRouteKey } = useRouter();
  const orderRouteId = getRouteKey("ORDER_MANAGEMENT", "id");
  const labRouteId = getRouteKey("LAB_REPORT", "id");
  const { routeState: orderState } = useRouteInfo(orderRouteId);
  const { statsList } = orderState.state as IOrderManagementState;
  const { routeState: labReportState } = useRouteInfo(labRouteId);
  const { statsList: labReportStatsList } = (labReportState?.state || {}) as ILabReportListState;
  const sampleCollected = (labReportStatsList as any)?.sample_collected;
  // RBAC: PWL_SALES_TEAM_VIEW — legacy roles: sales_ops, sales_admin, central_sales_lead
  const isBD = useFeatureAccess("PWL_SALES_TEAM_VIEW");
  // RBAC: PWL_DOCTOR_VIEW — legacy role: doctor
  const isDoctor = useFeatureAccess("PWL_DOCTOR_VIEW");
  /* Redux State Ends */

  /* Actions / Handlers Starts */
  const handelClick = (id: number) => {
    const filterBody = {
      ...filters,
      booking_statuses:
        id === 0
          ? (["Upcoming", "Waiting", "Attending", "Completed", "Waiting", "Cancelled"] as ValidBookingStatusesNew[])
          : ([statsMapping[id]] as ValidBookingStatusesNew[]),
    };

    dispatch(
      setPWLFilters({
        filters: { ...filterBody, created_by: null },
      }),
    );

    setStatCard(statsMapping[id]);
  };

  useEffect(() => {
    setStatCard("Total");
  }, []);

  /* Actions / Handlers Ends */

  useEffect(() => {
    if (filters && filters?.booking_statuses && filters?.booking_statuses?.length > 0 && !initialFetched) {
      if (filters?.booking_statuses?.length === 5) {
        setStatCard("Total");
        setInitialFetched(true);
      } else {
        setStatCard(filters?.booking_statuses[0]);
        setInitialFetched(true);
      }
    }
  }, [filters?.booking_statuses, initialFetched]);

  useEffect(() => {
    if (isStatsLoading || hasStatsData) {
      setHasRequestedStatsAtLeastOnce(true);
    }
  }, [isStatsLoading, hasStatsData]);

  const handelView = () => {
    navigate(`/app/qc-orders?status=2`);
  };

  const handelUpload = () => {
    navigate(`/app/lab-report/internal`);
  };

  const shouldShowLoader = isStatsLoading || (!hasRequestedStatsAtLeastOnce && !hasStatsData);
  const responsiveStatsContainerClassName = "flex w-full gap-3 overflow-x-auto lg:gap-[20px]";
  const statsCardContainerClassName = "min-w-[10rem] flex-none pt-[14px] pb-[10px] items-center lg:min-w-0 lg:flex-1";

  return (
    <div className="w-full lg:py-2">
      {!shouldShowLoader ? (
        hasStatsData ? (
          <>
            <div className={responsiveStatsContainerClassName}>
              {Object.keys(waitingStats[0]).map((key: string, id: number) => {
                const isSelected = currentStat?.toLowerCase() === key.toLowerCase();
                return (
                  <StatsCard
                    key={id}
                    onClick={() => handelClick(id)}
                    selected={isSelected}
                    unselectedContainerClassName="border-gray-300"
                    containerClassName={statsCardContainerClassName}
                    value={waitingStats[0][key]?.count}
                    label={key === "Upcoming" ? (
                    <>
                      Upcoming & <br /> No Show
                    </>
                  ) : (
                    key
                  )}
                  
                    valueClassName="text-[1.25rem] font-semibold "
                    labelClassName="text-[0.75rem] font-semibold text-center h-[24px] flex justify-center items-start"
                  />
                );
              })}
         

             {!(isBD || isDoctor) && ( 
              <>
          
              <StatsCard
                onClick={() => handelView()}
                unselectedContainerClassName={clsx(
                  statsList?.confirmed !== 0 ? "border-[#FAE2A6] bg-[#FFFAE9]" : "border-gray-300 bg-white ",
                )}
                containerClassName={statsCardContainerClassName}
                value={statsList?.confirmed}
                label="Pending for Packing"
                valueClassName="text-[1.25rem] font-semibold"
                labelClassName="text-[0.75rem] font-semibold text-center h-[24px] flex justify-center items-start"
                valueSuffix="Order"
                valueSuffixClassName="text-[0.875rem] font-semibold"
              />

              <StatsCard
                onClick={() => handelUpload()}
                unselectedContainerClassName={clsx(
                  sampleCollected !== 0 ? "border-[#FAE2A6] bg-[#FFFAE9]" : "border-gray-300 bg-white ",
                )}
                containerClassName={statsCardContainerClassName}
                value={sampleCollected}
                label={sampleCollected == 0 ? "Pending to Upload" : "Require to Upload"}
                valueClassName="text-[1.25rem] font-semibold"
                labelClassName="text-[0.75rem] font-semibold text-center h-[24px] flex justify-center items-start"
                valueSuffix="Lab Reports"
                valueSuffixClassName="text-[0.875rem] font-semibold"
              />
                  </>)
            }
     
               </div>
          </>
        ) : (
          <div className={responsiveStatsContainerClassName}>
            {Object.keys(emptyStates[0]).map((key: string, id: number) => {
              const isSelected = currentStat?.toLowerCase() === key.toLowerCase();
              return (
                <StatsCard
                  key={id}
                  onClick={() => handelClick(id)}
                  selected={isSelected}
                  unselectedContainerClassName="border-gray-300"
                  containerClassName={statsCardContainerClassName}
                  value={emptyStates[0][key]?.count}
                  label={key === "Upcoming" ? (
                    <>
                      Upcoming & <br /> No Show
                    </>
                  ) : (
                    key
                  )}
                  valueClassName="text-[1.25rem] font-semibold"
                  labelClassName="text-[0.75rem] font-semibold text-center h-[24px] flex justify-center items-start"
                />
              );
            })}
          </div>
        )
      ) : (
        <StatsCardsSkeleton />
      )}
    </div>
  );
};
export default PatientWaitingListStats;
